import React, { useEffect, useRef, useState } from "react";
import { Badge, Button, Form, ListGroup, Modal, Spinner } from "react-bootstrap";
import ReactQuill from "react-quill";

import styles from "../../styles/SupportTickets.module.css";

import "quill/dist/quill.snow.css";

const supportPrimaryBtnClass = `${styles.SupportBtn} ${styles.SupportPrimaryBtn}`;

/**
 * Normalizes attachment objects from the service-desk API and legacy shapes.
 */
const getAttachmentDownloadInfo = (file, idx) => {
  if (typeof file === "string") {
    const url = file;
    return {
      key: `att-str-${idx}`,
      url,
      label: `Image ${idx + 1}`,
      downloadName: `attachment-${idx + 1}`,
    };
  }
  const url = file?.file_url ?? file?.url ?? file?.file ?? file?.path ?? "";
  const label =
    file?.original_filename ?? file?.name ?? file?.filename ?? `Image ${idx + 1}`;
  const downloadName =
    file?.original_filename ?? file?.filename ?? file?.name ?? `attachment-${idx + 1}`;
  const key = file?.id != null ? `att-${file.id}` : `att-${idx}-${downloadName}`;
  return { key, url, label, downloadName };
};

const formatFileSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const TicketPreviewModal = ({
  show,
  onHide,
  isLoading,
  selectedTicket,
  editingReplyId,
  editingReplyText,
  setEditingReplyText,
  isSavingReplyEdit,
  onCancelEditReply,
  onSaveEditReply,
  onStartEditReply,
  onAskDeleteReply,
  replyComposerResetKey,
  isSubmittingReply,
  onReply,
  replyAttachmentFiles = [],
  onReplyAttachmentsAdd,
  onReplyAttachmentRemove,
}) => {
  const [draftHtml, setDraftHtml] = useState("");
  const replyFileInputRef = useRef(null);
  const isClosed = String(selectedTicket?.status ?? "").toLowerCase() === "closed";

  useEffect(() => {
    if (!show) return;
    setDraftHtml("");
  }, [show, replyComposerResetKey]);

  const handleReplyFileChange = (event) => {
    const { files } = event.target;
    if (files?.length) {
      onReplyAttachmentsAdd?.(files);
    }
    event.target.value = "";
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      scrollable
      className={styles.SupportModal}
      dialogClassName={styles.ModalWide}
    >
      <Modal.Header closeButton>
        <Modal.Title>Ticket Preview</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.ModalBodyScroll}>
        {isLoading && (
          <div className="text-center py-3">
            <Spinner animation="border" />
          </div>
        )}

        {!isLoading && selectedTicket && (
          <>
            <div className="d-flex align-items-baseline mb-2">
              <span className="mr-2">Subject:</span>
              <strong className="mb-0">{selectedTicket.title}</strong>
            </div>
            <p className="mb-2 d-flex justify-content-between align-items-center">
              <span>
                Ticket ID: <strong>#{selectedTicket.id}</strong>{" "}
                <span className="ml-3">
                  Category: <strong>{selectedTicket.category}</strong>
                </span>
              </span>
              <span>
                Status:{" "}
                <Badge variant={selectedTicket.status === "open" ? "success" : "secondary"}>
                  {selectedTicket.status}
                </Badge>
              </span>
            </p>
            <p>{selectedTicket.description}</p>

            <hr />
            <h6>Replies</h6>
            {selectedTicket.replies.length ? (
              <div className={`${styles.ChatThread} mb-3`}>
                <ListGroup className={styles.RepliesList}>
                  {selectedTicket.replies.map((reply) => (
                    <ListGroup.Item
                      key={reply.id}
                      className={`d-flex ${styles.ReplyItem}`}
                      style={{ justifyContent: reply.isSelf ? "flex-end" : "flex-start" }}
                    >
                      <div
                        style={{
                          maxWidth: "75%",
                          width: "fit-content",
                          flex: "0 1 auto",
                          paddingRight: reply.isSelf ? 0 : 12,
                          paddingLeft: reply.isSelf ? 12 : 0,
                        }}
                      >
                        <small
                          className={`text-muted d-block ${styles.ReplyMeta}`}
                          style={{ textAlign: reply.isSelf ? "right" : "left" }}
                        >
                          {reply.isSelf ? "You" : "Admin"}
                          {reply.createdAt ? ` - ${reply.createdAt}` : ""}
                        </small>

                        {editingReplyId === reply.id ? (
                          <Form onSubmit={onSaveEditReply}>
                            <Form.Group className="mb-2">
                              <Form.Control
                                as="textarea"
                                rows={3}
                                value={editingReplyText}
                                onChange={(event) => setEditingReplyText(event.target.value)}
                                disabled={isSavingReplyEdit}
                              />
                            </Form.Group>
                            <div className="d-flex justify-content-end">
                              <Button
                                variant="secondary"
                                className={`mr-2 ${styles.SupportBtn}`}
                                onClick={onCancelEditReply}
                                disabled={isSavingReplyEdit}
                              >
                                Cancel
                              </Button>
                              <Button
                                type="submit"
                                disabled={isSavingReplyEdit}
                                className={supportPrimaryBtnClass}
                                variant="primary"
                              >
                                {isSavingReplyEdit ? "Saving..." : "Save"}
                              </Button>
                            </div>
                          </Form>
                        ) : (
                          (() => {
                            const message = String(reply.message ?? "");
                            const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(message);

                            if (looksLikeHtml) {
                              return (
                                <div
                                  className={`${styles.ChatBubble} ${
                                    reply.isSelf ? styles.ChatBubbleSelf : styles.ChatBubbleOther
                                  }`}
                                  style={{
                                    wordBreak: "break-word",
                                    textAlign: "left",
                                  }}
                                  dangerouslySetInnerHTML={{ __html: message }}
                                />
                              );
                            }

                            return (
                              <div
                                className={`${styles.ChatBubble} ${
                                  reply.isSelf ? styles.ChatBubbleSelf : styles.ChatBubbleOther
                                }`}
                                style={{
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  textAlign: "left",
                                }}
                              >
                                {message}
                              </div>
                            );
                          })()
                        )}

                        {editingReplyId !== reply.id &&
                          Array.isArray(reply.attachments) &&
                          reply.attachments.length > 0 && (
                            <div
                              className={`mt-2 ${styles.ReplyMessageAttachments}`}
                              style={{ textAlign: reply.isSelf ? "right" : "left" }}
                            >
                              <div
                                className={`small text-muted mb-1 ${styles.ReplyMessageAttachmentsSummary}`}
                              >
                                {reply.attachments.length === 1
                                  ? "1 image attached"
                                  : `${reply.attachments.length} images attached`}
                              </div>
                              <ul
                                className={`${styles.ReplyMessageAttachmentLinks} mb-0`}
                                style={{
                                  textAlign: reply.isSelf ? "right" : "left",
                                }}
                              >
                                {reply.attachments.map((file, idx) => {
                                  const info = getAttachmentDownloadInfo(file, idx);
                                  if (!info.url) return null;
                                  return (
                                    <li key={`${reply.id}-${file?.id ?? idx}-${info.key}`}>
                                      <a
                                        href={info.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        download={info.downloadName}
                                        className={styles.ReplyMessageAttachmentLink}
                                        title={info.label}
                                      >
                                        {info.label}
                                      </a>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                      </div>

                      {/* Reply edit/delete — uncomment when ready (requires Dropdown import). */}
                      {/* {reply.isSelf && (
                        <div style={{ marginLeft: 8 }}>
                          <Dropdown alignRight>
                            <Dropdown.Toggle
                              variant="light"
                              size="sm"
                              className={styles.DotsToggle}
                            >
                              <span style={{ fontSize: "1.2rem", lineHeight: 1 }}>⋯</span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item
                                onClick={(event) => onStartEditReply(event, reply)}
                                disabled={editingReplyId != null}
                              >
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={(event) => onAskDeleteReply(event, reply.id)}
                                disabled={editingReplyId != null}
                              >
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      )} */}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            ) : (
              <p className="text-muted">No replies yet.</p>
            )}

            {isClosed ? (
              <p className="text-muted mb-0">
                This ticket is closed, so you can’t add more replies. If you still need help,
                please raise a new support ticket.
              </p>
            ) : (
              <Form
                onSubmit={(event) => {
                  onReply(event, draftHtml);
                }}
              >
                <Form.Group controlId="ticketReply">
                  <Form.Label>Add Reply</Form.Label>
                  <div className={`${styles.QuillEditor} ${styles.ReplyTextArea}`}>
                    <ReactQuill
                      key={replyComposerResetKey}
                      theme="snow"
                      value={draftHtml}
                      onChange={setDraftHtml}
                      placeholder="Write a reply..."
                      readOnly={isSubmittingReply}
                      modules={{
                        toolbar: [
                          ["bold", "italic", "underline", "strike"],
                          [{ list: "ordered" }, { list: "bullet" }],
                          ["link"],
                        ],
                      }}
                      formats={["bold", "italic", "underline", "strike", "list", "link"]}
                    />
                  </div>
                </Form.Group>

                <Form.Group controlId="ticketReplyAttachments">
                  <Form.Label>Attach images to this reply (optional)</Form.Label>
                  <Form.Text muted className="d-block mb-2">
                    Images only. Multiple files allowed; each must be 5 MB or less.
                  </Form.Text>
                  <div className="d-flex align-items-center flex-wrap">
                    <input
                      ref={replyFileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="d-none"
                      onChange={handleReplyFileChange}
                      disabled={isSubmittingReply}
                      tabIndex={-1}
                    />
                    <Button
                      type="button"
                      variant="primary"
                      className={supportPrimaryBtnClass}
                      onClick={() => replyFileInputRef.current?.click()}
                    >
                      Browse file
                    </Button>
                  </div>
                  {replyAttachmentFiles.length > 0 && (
                    <div className={`${styles.ReplyStagedFiles} mt-3`}>
                      {replyAttachmentFiles.map((file, index) => (
                        <div
                          key={`${file.name}-${file.lastModified}-${index}`}
                          className={styles.ReplyStagedFileRow}
                        >
                          <div className={styles.ReplyStagedFileMeta}>
                            <span className={styles.ReplyStagedFileName} title={file.name}>
                              {file.name}
                            </span>
                            <span className="text-muted small">{formatFileSize(file.size)}</span>
                          </div>
                          <Button
                            type="button"
                            variant="outline-danger"
                            size="sm"
                            className={styles.CreateAttachmentRemove}
                            disabled={isSubmittingReply}
                            onClick={() => onReplyAttachmentRemove?.(index)}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>

                <div className="d-flex justify-content-end">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isSubmittingReply}
                    className={supportPrimaryBtnClass}
                    onMouseDown={(event) => event.preventDefault()}
                  >
                    {isSubmittingReply ? "Replying..." : "Reply"}
                  </Button>
                </div>
              </Form>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default TicketPreviewModal;
